/**
 * CobrancaMemoriaCalculoModal
 *
 * Modal premium que exibe a "memória de cálculo" de uma cobrança — útil para
 * o usuário conferir como o valor da cobrança (em especial ciclos pro-rata e
 * ajustes mid-cycle) foi composto: resumo financeiro fechado, janela de
 * cobrança, composição por item (com items_diff quando mid-cycle), origem
 * dos créditos consumidos, fórmula aplicada e timeline de eventos.
 *
 * Read-only. Todos os valores vêm da RPC `get_cobranca_calculation_memory`.
 *
 * @author Módulo Financeiro
 * RELEASE: 2026-06-22 v1.1.0 - Reconstrução do conteúdo para refletir a RPC v1.1.0:
 *                              narrativa contextual, bloco "Resumo" com aritmética
 *                              fechada (subtotal − descontos − crédito = total),
 *                              tabela items_diff para mid-cycle, lista de créditos
 *                              do cliente, fórmula adaptada por tipo, fim do
 *                              falso-positivo de divergência quando explicada por
 *                              crédito aplicado, timeline cronológica completa.
 * RELEASE: 2026-06-17 v1.0.0 - Versão inicial.
 */

import { useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { InlineNotice } from '@/components/ui/inline-notice';
import {
  Calculator,
  Calendar,
  ListTree,
  History,
  Sigma,
  Copy,
  Loader2,
  Wallet,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { formatCurrency } from '@/lib/currency-utils';
import { parseDateLocal, cn } from '@/lib/utils';
import {
  useCobrancaCalculationMemory,
  type CobrancaMemoriaCalculo,
  type CobrancaProRataItemDiff,
  type CobrancaTipo,
} from '@/hooks/financial/useCobrancaCalculationMemory';
import { useTenantSelfCalculationMemory } from '@/hooks/tenant/useTenantBilling';

interface CobrancaMemoriaCalculoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cobrancaId: string | null | undefined;
  /**
   * Origem da consulta:
   *  - 'master' (default): RPC `get_cobranca_calculation_memory` (tenant master).
   *  - 'self': RPC `tenant_billing_get_self_calculation_memory` (tenant cliente,
   *    sanitizada). Usado nas telas /tenant/plans (BillingHub).
   */
  source?: 'master' | 'self';
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers locais de formatação
// ─────────────────────────────────────────────────────────────────────────────

function fmtDate(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    const d = value.length === 10 ? parseDateLocal(value) : new Date(value);
    return format(d, 'dd/MM/yyyy', { locale: ptBR });
  } catch {
    return '—';
  }
}

function fmtDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  try {
    return format(new Date(value), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return '—';
  }
}

function fmtFator(factor: number | null | undefined): string {
  if (factor == null) return '—';
  return factor.toFixed(6).replace('.', ',');
}

/** Rótulo amigável para o tipo da cobrança (vem da RPC v1.1.0). */
function describeTipo(tipo: CobrancaTipo): string {
  switch (tipo) {
    case 'mid_cycle_proration':
      return 'Ajuste pro-rata · mid-cycle';
    case 'pro_rata_criacao':
      return 'Pro-rata na criação';
    case 'ciclo_regular':
      return 'Cobrança regular';
    case 'cobranca_avulsa':
      return 'Cobrança avulsa';
    default:
      return tipo;
  }
}

/** Rótulo PT-BR para tipos de evento de auditoria. */
function describeEvent(type: string): string {
  switch (type) {
    case 'pro_rata_applied_on_create':
      return 'Pro-rata aplicado na criação';
    case 'mid_cycle_proration_applied':
      return 'Ajuste pro-rata aplicado';
    case 'itens_atualizados':
      return 'Itens da assinatura atualizados';
    case 'plan_change_applied':
      return 'Mudança de plano aplicada';
    case 'credit_consumed':
      return 'Crédito consumido';
    case 'credit_generated':
      return 'Crédito gerado';
    case 'subscription_updated':
      return 'Assinatura atualizada';
    case 'subscription_created':
      return 'Assinatura criada';
    case 'subscription_cycle_charged':
      return 'Ciclo cobrado';
    default:
      return type;
  }
}

/** Origem amigável para o crédito do cliente. */
function describeCreditoOrigem(origem: string): string {
  switch (origem) {
    case 'downgrade':
      return 'Sobra de downgrade';
    case 'plan_change':
      return 'Mudança de plano';
    case 'upgrade_credit':
      return 'Crédito de upgrade';
    case 'refund':
      return 'Reembolso';
    case 'manual':
      return 'Lançamento manual';
    default:
      return origem;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Geração do texto "Copiar memória"
// ─────────────────────────────────────────────────────────────────────────────

function buildMemoriaTexto(m: CobrancaMemoriaCalculo): string {
  const c = m.cobranca;
  const t = m.totais;
  const linhas: string[] = [];

  linhas.push(`MEMÓRIA DE CÁLCULO — ${describeTipo(c.tipo)}`);
  if (c.ciclo_numero != null) linhas.push(`Ciclo #${c.ciclo_numero}`);
  if (c.ciclo_origem != null) linhas.push(`Originada do Ciclo #${c.ciclo_origem}`);
  linhas.push('');
  linhas.push(c.narrativa);
  linhas.push('');
  linhas.push('RESUMO');
  linhas.push(`  Subtotal (itens):  ${formatCurrency(t.subtotal_itens)}`);
  linhas.push(`  Descontos:         ${formatCurrency(t.descontos)}`);
  linhas.push(`  Crédito aplicado:  ${formatCurrency(t.credito_aplicado)}`);
  linhas.push(`  TOTAL COBRADO:     ${formatCurrency(t.total_cobrado)}`);
  if (t.delta_total != null) linhas.push(`  Delta (mid-cycle): ${formatCurrency(t.delta_total)}`);
  linhas.push('');
  linhas.push('JANELA');
  linhas.push(`  Período: ${fmtDate(c.periodo_inicio)} → ${fmtDate(c.periodo_fim)}`);
  linhas.push(`  Dias:    ${m.calculo.dias_periodo ?? '—'}`);
  linhas.push(`  Fator:   ${fmtFator(m.calculo.fator_oficial)} (fonte: ${m.calculo.factor_source})`);
  linhas.push('');
  linhas.push('ITENS DA COBRANÇA');
  m.itens.forEach((i) => {
    linhas.push(
      `  • ${i.descricao ?? '—'} — Qtd ${i.quantidade} × ${formatCurrency(i.valor_unitario)} ` +
        `= ${formatCurrency(i.total)}`,
    );
  });
  if (c.pro_rata_metadata?.items_diff?.length) {
    linhas.push('');
    linhas.push('DELTA POR ITEM (mid-cycle)');
    c.pro_rata_metadata.items_diff.forEach((d) => {
      const nome = d.novo?.descricao ?? d.antigo?.descricao ?? '—';
      linhas.push(
        `  • ${nome}: débito ${formatCurrency(d.debito)} − crédito ${formatCurrency(d.credito)} = ${formatCurrency(d.delta)}`,
      );
    });
  }
  if (m.creditos.lista_disponivel.length > 0) {
    linhas.push('');
    linhas.push(`SALDO DE CRÉDITO DO CLIENTE: ${formatCurrency(m.creditos.saldo_disponivel_atual)}`);
  }
  return linhas.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Componente principal
// ─────────────────────────────────────────────────────────────────────────────

export function CobrancaMemoriaCalculoModal({
  open,
  onOpenChange,
  cobrancaId,
  source = 'master',
}: CobrancaMemoriaCalculoModalProps) {
  // Seleciona a fonte: tenant master (admin) ou self (tenant cliente sanitizado).
  const masterQuery = useCobrancaCalculationMemory(cobrancaId, open && source === 'master');
  const selfQuery = useTenantSelfCalculationMemory(cobrancaId, open && source === 'self');
  const { data, isLoading, error } = source === 'self' ? selfQuery : masterQuery;

  // Divergência real = a parte que NÃO é explicada por crédito aplicado.
  // Quando creditoAplicado >= |divergencia|, não há "erro" — apenas crédito consumido.
  const divergenciaInexplicada = useMemo(() => {
    if (!data) return 0;
    const div = Math.abs(data.calculo.divergencia_vs_persistido);
    const credito = data.totais.credito_aplicado;
    // Sobra positiva absorvida por crédito é esperada. Qualquer outra diferença é alarme.
    return Math.max(0, Math.round((div - credito) * 100) / 100);
  }, [data]);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(buildMemoriaTexto(data));
      toast.success('Memória de cálculo copiada');
    } catch {
      toast.error('Não foi possível copiar');
    }
  };

  const cobranca = data?.cobranca;
  const totais = data?.totais;
  const itemsDiff = cobranca?.pro_rata_metadata?.items_diff ?? null;
  const isMidCycle = cobranca?.tipo === 'mid_cycle_proration';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] h-[95dvh] sm:h-[85dvh] p-0 flex flex-col overflow-hidden">
        <DialogHeader className="px-4 sm:px-6 py-3 border-b">
          <DialogTitle className="flex flex-wrap items-center gap-2 text-base">
            <Calculator className="h-4 w-4 text-primary" />
            Memória de cálculo
            {cobranca && (
              <span className="text-muted-foreground font-normal">
                — {describeTipo(cobranca.tipo)}
              </span>
            )}
            {cobranca?.ciclo_numero != null && cobranca.tipo !== 'mid_cycle_proration' && (
              <Badge variant="secondary" className="text-[10px]">
                Ciclo #{cobranca.ciclo_numero}
              </Badge>
            )}
            {cobranca?.ciclo_origem != null && (
              <Badge variant="secondary" className="text-[10px]">
                Originada do Ciclo #{cobranca.ciclo_origem}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {cobranca?.narrativa ?? 'Detalhamento de como o valor desta cobrança foi calculado.'}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 px-4 sm:px-6 py-4">
          {isLoading && (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span className="text-sm">Carregando memória…</span>
            </div>
          )}

          {error && (
            <InlineNotice variant="error">
              Não foi possível carregar a memória de cálculo desta cobrança.
            </InlineNotice>
          )}

          {data && cobranca && totais && (
            <div className="space-y-6">
              {/* ═══ Resumo financeiro (aritmética fechada) ═══ */}
              <section>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <SummaryCard
                    label="Subtotal dos itens"
                    value={formatCurrency(totais.subtotal_itens)}
                  />
                  <SummaryCard
                    label="Descontos"
                    value={totais.descontos > 0 ? `-${formatCurrency(totais.descontos)}` : formatCurrency(0)}
                    tone={totais.descontos > 0 ? 'negative' : 'muted'}
                  />
                  <SummaryCard
                    label="Crédito aplicado"
                    value={totais.credito_aplicado > 0 ? `-${formatCurrency(totais.credito_aplicado)}` : formatCurrency(0)}
                    tone={totais.credito_aplicado > 0 ? 'credit' : 'muted'}
                    hint={
                      totais.credito_aplicado > 0
                        ? 'Diferença entre soma dos itens e o valor cobrado — absorvida por crédito/sobra do cliente.'
                        : undefined
                    }
                  />
                  <SummaryCard
                    label="Total cobrado"
                    value={formatCurrency(totais.total_cobrado)}
                    tone="primary"
                  />
                </div>
                {totais.delta_total != null && isMidCycle && (
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Delta líquido do ajuste mid-cycle:{' '}
                    <span className="font-medium text-foreground">
                      {formatCurrency(totais.delta_total)}
                    </span>
                  </div>
                )}
              </section>

              <Separator />

              {/* ═══ Janela de cobrança ═══ */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Janela de cobrança</h3>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                  <Field label="Início do período" value={fmtDate(cobranca.periodo_inicio)} />
                  <Field label="Fim do período" value={fmtDate(cobranca.periodo_fim)} />
                  <Field label="Dias do período" value={String(data.calculo.dias_periodo ?? '—')} />
                  <Field label="Dias cobrados" value={String(data.calculo.dias_cobrados ?? '—')} />
                  {data.assinatura && (
                    <>
                      <Field
                        label="Data âncora"
                        value={fmtDate(data.assinatura.billing_anchor_date)}
                      />
                      <Field label="Frequência" value={data.assinatura.frequencia} />
                      <Field
                        label="Ciclo"
                        value={`${cobranca.ciclo_numero ?? '—'} / ${data.assinatura.ciclos_total ?? '∞'}`}
                      />
                      <Field
                        label="Fator oficial"
                        value={fmtFator(data.calculo.fator_oficial)}
                        hint={`fonte: ${data.calculo.factor_source}`}
                      />
                    </>
                  )}
                </div>
                <div className="mt-2 text-[11px] text-muted-foreground">
                  {isMidCycle
                    ? 'Período restante do ciclo origem no qual o ajuste pro-rata foi aplicado.'
                    : 'Período coberto por esta cobrança.'}
                </div>
              </section>

              <Separator />

              {/* ═══ Itens da cobrança (persistidos) ═══ */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <ListTree className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Itens da cobrança</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                        <th className="text-left py-1.5 pr-2">Produto</th>
                        <th className="text-center py-1.5 px-2">Qtd</th>
                        <th className="text-right py-1.5 px-2">Cheio</th>
                        <th className="text-right py-1.5 px-2">Fator</th>
                        <th className="text-right py-1.5 px-2">Aplicado</th>
                        <th className="text-right py-1.5 px-2">Desc.</th>
                        <th className="text-right py-1.5 pl-2">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.itens.map((it) => {
                        // v1.11.3: "Aplicado" representa o valor da linha pós pro-rata
                        // (Qtd × Cheio × Fator). Quando não há fator pro-rata aplicado,
                        // cai para Qtd × valor_unitario (linha cheia).
                        const qty = Number(it.quantidade ?? 0);
                        const cheio = Number(it.valor_cheio_unitario ?? 0);
                        const fator = Number(it.pro_rata_factor ?? 0);
                        const aplicado =
                          fator > 0 && fator < 1 && cheio > 0
                            ? qty * cheio * fator
                            : qty * Number(it.valor_unitario ?? 0);
                        return (
                          <tr key={it.id} className="border-b border-border/30">
                            <td className="py-1.5 pr-2 font-medium">{it.descricao ?? '—'}</td>
                            <td className="text-center py-1.5 px-2">{it.quantidade}</td>
                            <td className="text-right py-1.5 px-2 text-muted-foreground">
                              {formatCurrency(cheio)}
                            </td>
                            <td className="text-right py-1.5 px-2 font-mono">
                              {fmtFator(it.pro_rata_factor)}
                            </td>
                            <td className="text-right py-1.5 px-2">
                              {formatCurrency(aplicado)}
                            </td>
                            <td className="text-right py-1.5 px-2 text-destructive">
                              {it.desconto && it.desconto > 0
                                ? `-${formatCurrency(it.desconto)}`
                                : '—'}
                            </td>
                            <td className="text-right py-1.5 pl-2 font-semibold">
                              {formatCurrency(it.total)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ═══ Composição do delta (mid-cycle): items_diff ═══ */}
              {isMidCycle && itemsDiff && itemsDiff.length > 0 && (
                <>
                  <Separator />
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold">
                        Composição do delta · antes × depois
                      </h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                            <th className="text-left py-1.5 pr-2">Item</th>
                            <th className="text-right py-1.5 px-2">Antes</th>
                            <th className="text-right py-1.5 px-2">Depois</th>
                            <th className="text-right py-1.5 px-2">Crédito</th>
                            <th className="text-right py-1.5 px-2">Débito</th>
                            <th className="text-right py-1.5 pl-2">Delta</th>
                          </tr>
                        </thead>
                        <tbody>
                          {itemsDiff.map((d, idx) => (
                            <ItemDiffRow key={idx} diff={d} />
                          ))}
                        </tbody>
                        {/* Totalizador: soma de Crédito, Débito e Delta no rodapé */}
                        <tfoot className="bg-muted/30 border-t-2 border-border/60">
                          {(() => {
                            const totalCredito = itemsDiff.reduce((acc, d) => acc + (d.credito || 0), 0);
                            const totalDebito = itemsDiff.reduce((acc, d) => acc + (d.debito || 0), 0);
                            const totalDelta = itemsDiff.reduce((acc, d) => acc + (d.delta || 0), 0);
                            return (
                              <tr className="font-semibold">
                                <td colSpan={3} className="py-1.5 pr-2 text-right uppercase text-[10px] tracking-wider text-muted-foreground">
                                  Total
                                </td>
                                <td className="text-right py-1.5 px-2 text-emerald-600 tabular-nums">
                                  {formatCurrency(totalCredito)}
                                </td>
                                <td className="text-right py-1.5 px-2 tabular-nums">
                                  {formatCurrency(totalDebito)}
                                </td>
                                <td
                                  className={cn(
                                    'text-right py-1.5 pl-2 tabular-nums font-bold',
                                    totalDelta < 0 && 'text-emerald-600',
                                  )}
                                >
                                  {formatCurrency(totalDelta)}
                                </td>
                              </tr>
                            );
                          })()}
                        </tfoot>

                      </table>
                    </div>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Cada linha mostra o item antes e depois do ajuste. Itens removidos geram{' '}
                      <span className="text-emerald-600 font-medium">crédito</span>; itens
                      adicionados/aumentados geram{' '}
                      <span className="text-foreground font-medium">débito</span>. O delta é a
                      diferença líquida cobrada nesta cobrança.
                    </p>
                  </section>
                </>
              )}

              {/* ═══ Saldo de crédito do cliente ═══ */}
              {data.creditos.lista_disponivel.length > 0 && (
                <>
                  <Separator />
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <Wallet className="h-4 w-4 text-emerald-600" />
                      <h3 className="text-sm font-semibold">Créditos do cliente (saldo atual)</h3>
                      <Badge variant="secondary" className="ml-auto text-[10px]">
                        Total: {formatCurrency(data.creditos.saldo_disponivel_atual)}
                      </Badge>
                    </div>
                    <ul className="space-y-2">
                      {data.creditos.lista_disponivel.map((cr) => (
                        <li
                          key={cr.id}
                          className="border border-border/40 rounded-md px-3 py-2 text-xs"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">
                              {describeCreditoOrigem(cr.origem)}
                            </span>
                            <span className="text-emerald-600 font-semibold">
                              {formatCurrency(cr.saldo)}
                            </span>
                          </div>
                          <div className="text-muted-foreground text-[10px] flex flex-wrap gap-x-3 mt-0.5">
                            <span>Gerado em {fmtDateTime(cr.created_at)}</span>
                            <span>
                              Original {formatCurrency(cr.valor)} · Usado{' '}
                              {formatCurrency(cr.valor_usado)}
                            </span>
                            {cr.expira_em && <span>Expira em {fmtDate(cr.expira_em)}</span>}
                          </div>
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      Saldo atual disponível para abater cobranças futuras desta assinatura/cliente.
                    </p>
                  </section>
                </>
              )}

              <Separator />

              {/* ═══ Fórmula aplicada (adaptada por tipo) ═══ */}
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Sigma className="h-4 w-4 text-primary" />
                  <h3 className="text-sm font-semibold">Fórmula aplicada</h3>
                </div>
                <pre className="bg-muted/40 rounded-md p-3 text-[11px] font-mono whitespace-pre-wrap leading-relaxed text-foreground">
                  {buildFormulaText(data)}
                </pre>
              </section>

              {/* ═══ Validação cruzada ═══ */}
              {divergenciaInexplicada > 0.01 ? (
                <InlineNotice variant="warning">
                  Divergência inexplicada de{' '}
                  <strong>{formatCurrency(divergenciaInexplicada)}</strong> entre o total calculado
                  e o valor persistido — não coberta pelo crédito aplicado. Abra um chamado.
                </InlineNotice>
              ) : totais.credito_aplicado > 0.01 ? (
                <InlineNotice variant="info">
                  Diferença de <strong>{formatCurrency(totais.credito_aplicado)}</strong> entre a
                  soma dos itens e o total cobrado é explicada pelo{' '}
                  <strong>crédito do cliente aplicado</strong> nesta cobrança. Nenhuma divergência
                  real.
                </InlineNotice>
              ) : (
                <InlineNotice variant="success">
                  Valor calculado bate exatamente com o valor cobrado — sem divergência.
                </InlineNotice>
              )}

              {/* ═══ Timeline de eventos ═══ */}
              {data.eventos.length > 0 && (
                <>
                  <Separator />
                  <section>
                    <div className="flex items-center gap-2 mb-3">
                      <History className="h-4 w-4 text-primary" />
                      <h3 className="text-sm font-semibold">Linha do tempo</h3>
                    </div>
                    <ul className="space-y-2">
                      {data.eventos.map((ev, idx) => (
                        <li
                          key={idx}
                          className="border-l-2 border-primary/40 pl-3 py-1 text-xs"
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-medium">{describeEvent(ev.event_type)}</span>
                            <span className="text-muted-foreground text-[10px]">
                              {fmtDateTime(ev.created_at)}
                            </span>
                          </div>
                          <div className="text-muted-foreground text-[11px]">
                            {ev.actor_full_name ?? 'Sistema'}
                            {ev.ciclo_numero != null && ` · ciclo #${ev.ciclo_numero}`}
                            {ev.event_source ? ` · ${ev.event_source}` : ''}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </section>
                </>
              )}
            </div>
          )}
        </ScrollArea>

        <div className="border-t px-4 sm:px-6 py-3 flex items-center justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            disabled={!data}
            className="gap-1.5"
          >
            <Copy className="h-3.5 w-3.5" />
            Copiar memória
          </Button>
          <Button size="sm" onClick={() => onOpenChange(false)}>
            Fechar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Fórmula textual gerada conforme o tipo da cobrança
// ─────────────────────────────────────────────────────────────────────────────

function buildFormulaText(data: CobrancaMemoriaCalculo): string {
  const c = data.cobranca;
  const calc = data.calculo;
  const t = data.totais;

  if (c.tipo === 'mid_cycle_proration') {
    const meta = c.pro_rata_metadata ?? {};
    return [
      `# Ajuste pro-rata mid-cycle (origem: Ciclo #${c.ciclo_origem ?? '—'})`,
      ``,
      `factor = dias_restantes / dias_periodo_ciclo_origem`,
      `       = ${fmtFator(calc.fator_oficial)}   (fonte: ${calc.factor_source})`,
      ``,
      `Para cada item alterado:`,
      `  credito_item = qtd_antiga × valor_antigo × factor`,
      `  debito_item  = qtd_nova   × valor_novo   × factor`,
      `  delta_item   = debito_item − credito_item`,
      ``,
      `delta_total       = Σ deltas                = ${formatCurrency(t.delta_total ?? 0)}`,
      `subtotal_itens    = Σ itens persistidos     = ${formatCurrency(t.subtotal_itens)}`,
      `descontos         =                          ${formatCurrency(t.descontos)}`,
      `credito_aplicado  = subtotal − descontos − total_cobrado = ${formatCurrency(t.credito_aplicado)}`,
      `total_cobrado (grava no banco) =              ${formatCurrency(t.total_cobrado)}`,
      meta.mode ? `\nmodo pro-rata: ${meta.mode}` : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  if (c.tipo === 'pro_rata_criacao') {
    return [
      `# Pro-rata aplicado na criação`,
      ``,
      `dias_periodo  = (${fmtDate(c.periodo_fim)} − ${fmtDate(c.periodo_inicio)}) + 1 = ${calc.dias_periodo ?? '—'}`,
      `dias_cobrados = ROUND(fator × dias_periodo)               = ${calc.dias_cobrados ?? '—'}`,
      `fator         = ${fmtFator(calc.fator_oficial)}   (fonte: ${calc.factor_source})`,
      ``,
      `Para cada item:`,
      `  valor_aplicado = ROUND(valor_cheio × fator; 2)`,
      `  total_item     = valor_aplicado × quantidade − desconto`,
      ``,
      `subtotal_itens    = ${formatCurrency(t.subtotal_itens)}`,
      `descontos         = ${formatCurrency(t.descontos)}`,
      `credito_aplicado  = ${formatCurrency(t.credito_aplicado)}`,
      `total_cobrado     = subtotal − descontos − credito_aplicado = ${formatCurrency(t.total_cobrado)}`,
    ].join('\n');
  }

  // ciclo_regular ou cobranca_avulsa
  return [
    `# Cobrança ${c.tipo === 'ciclo_regular' ? 'regular' : 'avulsa'}`,
    ``,
    `subtotal_itens    = Σ (valor_unitario × quantidade) = ${formatCurrency(t.subtotal_itens)}`,
    `descontos         =                                   ${formatCurrency(t.descontos)}`,
    `credito_aplicado  =                                   ${formatCurrency(t.credito_aplicado)}`,
    `total_cobrado     = subtotal − descontos − credito_aplicado = ${formatCurrency(t.total_cobrado)}`,
  ].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// Subcomponentes visuais
// ─────────────────────────────────────────────────────────────────────────────

function Field({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-foreground font-medium">{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </div>
  );
}

function SummaryCard({
  label,
  value,
  hint,
  tone = 'default',
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: 'default' | 'muted' | 'negative' | 'credit' | 'primary';
}) {
  const toneClass = {
    default: 'bg-muted/30',
    muted: 'bg-muted/20 text-muted-foreground',
    negative: 'bg-destructive/5 text-destructive',
    credit: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
    primary: 'bg-primary/10 text-primary',
  }[tone];

  return (
    <div
      className={cn(
        'rounded-md px-3 py-2 border border-border/40',
        toneClass,
      )}
      title={hint}
    >
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}

function ItemDiffRow({ diff }: { diff: CobrancaProRataItemDiff }) {
  const nome = diff.novo?.descricao ?? diff.antigo?.descricao ?? '—';
  const antesDesc = diff.antigo
    ? `${diff.antigo.quantidade ?? 1}× ${formatCurrency(diff.antigo.valor_unitario ?? 0)}`
    : '—';
  const depoisDesc = diff.novo
    ? `${diff.novo.quantidade ?? 1}× ${formatCurrency(diff.novo.valor_unitario ?? 0)}`
    : '—';

  const isPositivo = diff.delta > 0;
  const isNegativo = diff.delta < 0;

  return (
    <tr className={cn('border-b border-border/30', diff.skipped && 'opacity-60')}>
      <td className="py-1.5 pr-2 font-medium">
        {nome}
        {diff.skipped && (
          <span className="ml-1 text-[10px] text-muted-foreground">(sem mudança)</span>
        )}
      </td>
      <td className="text-right py-1.5 px-2 text-muted-foreground">{antesDesc}</td>
      <td className="text-right py-1.5 px-2">{depoisDesc}</td>
      <td className="text-right py-1.5 px-2 text-emerald-600">
        {diff.credito > 0 ? formatCurrency(diff.credito) : '—'}
      </td>
      <td className="text-right py-1.5 px-2">
        {diff.debito > 0 ? formatCurrency(diff.debito) : '—'}
      </td>
      <td
        className={cn(
          'text-right py-1.5 pl-2 font-semibold inline-flex items-center justify-end gap-1 w-full',
          isPositivo && 'text-foreground',
          isNegativo && 'text-emerald-600',
        )}
      >
        {isNegativo && <TrendingDown className="h-3 w-3" />}
        {isPositivo && <TrendingUp className="h-3 w-3" />}
        {formatCurrency(diff.delta)}
      </td>
    </tr>
  );
}
